/**
 * EVENTSFLO-CLIENT-SCOPED-VENUE-LOCATION-FILTERING-V1
 *
 * Canonical client-scoped venue visibility helper.
 *
 * Matches the backend rule in
 * `backend/server.py::_build_client_venue_visibility_filter`:
 *
 *   A venue is visible to a given client if ANY of:
 *     a) venue.client_id === clientId              (owned)
 *     b) venue.shared_client_ids includes clientId (shared)
 *     c) venue.copied_for_client_id === clientId   (client copy)
 *
 * Tenant-library venues (no client_id, no sharing, no copy) are NOT
 * visible for a given client — matching the platform-wide business
 * rule that unrelated venues must not leak between clients.
 *
 * When `clientId` is empty / null / undefined, `filterVenuesForClient`
 * returns an empty list — callers are expected to render an empty
 * state / "Select a Client first" prompt in that case.
 */

/**
 * @param {object} venue
 * @param {string} clientId
 * @returns {boolean}
 */
export function isVenueVisibleToClient(venue, clientId) {
  if (!venue || !clientId) return false;
  if (venue.client_id === clientId) return true;
  const shared = Array.isArray(venue.shared_client_ids) ? venue.shared_client_ids : [];
  if (shared.includes(clientId)) return true;
  if (venue.copied_for_client_id === clientId) return true;
  return false;
}

/**
 * @param {object[]} venues
 * @param {string} clientId
 * @returns {object[]}
 */
export function filterVenuesForClient(venues, clientId) {
  if (!Array.isArray(venues) || !clientId) return [];
  return venues.filter((v) => isVenueVisibleToClient(v, clientId));
}

/**
 * Denormalised visibility label — mirrors the backend `visibility_type`
 * denormalisation but is derived at runtime so we don't rely on the
 * field being present. Used by ClientDetailPage to badge owned vs
 * shared vs client-copy venues.
 *
 * @param {object} venue
 * @param {string} clientId
 * @returns {"owned"|"shared"|"client_copy"|"unrelated"}
 */
export function venueVisibilityLabelForClient(venue, clientId) {
  if (!venue || !clientId) return "unrelated";
  if (venue.copied_for_client_id === clientId || venue.is_client_copy === true) {
    return "client_copy";
  }
  if (venue.client_id === clientId) return "owned";
  const shared = Array.isArray(venue.shared_client_ids) ? venue.shared_client_ids : [];
  if (shared.includes(clientId)) return "shared";
  return "unrelated";
}
